import { ObjectValues } from '..';

export type Cart = {
  applied_coupon: AppliedCoupon | null;
  available_payment_methods: AvailablePaymentMethods;
  currency: string;
  digital_products_only: boolean;
  digital_products_present: boolean;
  expired_products_removed: number[];
  items: Item[];
  messages: Message[];
  nonsaleable_products_removed: number[];
  predetermined_consent_to_offers_and_updates: boolean | null;
  summary: Summary;
  total_items_count: number;
  user: User;
};

export type CouponType =
  | 'AmountCoupon'
  | 'FreeSampleCoupon'
  | 'FreeShippingCoupon'
  | 'GiftCertificateCoupon'
  | 'PercentOffCoupon'
  | 'VariablePercentOffCoupon';

export type AppliedCoupon = {
  amount: string;
  amount_in_buyer_currency: string | null;
  code: string;
  currency: Currency;
  display_name: string;
  expiry_date: string;
  full_discount: Discount;
  is_expired: boolean;
  sub_discount: Discount;
  type: CouponType;
};

export type Currency = {
  delimiter: string;
  id: number;
  iso: string;
  name: string;
  precision: number;
  prefix: string;
  separator: string;
  unit: string;
};

export type Discount = {
  amount: string;
  currency: Currency;
};

export type AvailablePaymentMethods = {
  afterpay: boolean;
  clearpay: boolean;
  klarna: boolean;
  paypal: boolean;
  sofort?: boolean;
};

export type Item = {
  artist_name: string | null;
  blank_item_id: string | null;
  delivery_dates_product_code: null;
  details: Details;
  fixed_quantity: boolean;
  ga_code: string | null;
  id: number;
  image_url: string;
  inventory_item_id: string | null;
  is_digital: boolean;
  item_price: number;
  item_price_ex_tax: number;
  marketing_product_type_id: string | null;
  marketing_sku: string | null;
  product_type_id: string | null;
  quantity: number;
  total_price: number;
  total_price_ex_tax: number;
  total_shipping_price: number;
  type: string;
  undiscounted_total_price: number;
  undiscounted_total_price_ex_tax: number;
  work_id: number | null;
  work_title: string | null;
  work_url: string;
};

export type Details = {
  options: string;
  type: string;
};

export type Message = {
  action_url?: string;
  applied?: boolean;
  discount_key?: null | string;
  discount_product_types?: string[];
  discount_product_type_ids?: string[];
  product_name?: null | string;
  severity: ObjectValues<typeof Severity>;
  text?: string;
  type: ObjectValues<typeof Type>;
  priority?: Number | null;
};

export const Severity = {
  Info: 'info',
  Success: 'success',
  Warning: 'warning',
  Error: 'error',
} as const;

export const Type = {
  ProductWarning: 'product_warning',
  DiscountSitewide: 'discount_sitewide',
  DiscountVolume: 'discount_volume',
  DiscountShipping: 'discount_shipping',
  FreeShippingOnProductApplied: 'free_shipping_on_product_applied',
  FlatRateShippingApplied: 'flat_rate_shipping_applied',
  NonsaleableProductRemoval: 'nonsaleable_product_removal',
  ExpiredProductRemoval: 'expired_product_removal',
} as const;

export type Summary = {
  estimated_shipping: EstimatedShipping;
  show_tax_separately: boolean;
  show_undiscounted_price: boolean;
  subtotal: number;
  subtotal_ex_tax: number;
  tax: Tax | null;
  total: number;
  totals: Totals;
  undiscounted_subtotal: number;
  undiscounted_subtotal_ex_tax: number;
  is_tax_computed: boolean;
};

export type EstimatedShipping = {
  allow_express: boolean;
  country_code: string;
  country_name: string;
  express: number;
  express_ex_tax: number;
  preference: ShippingPreference | null;
  shippingPreferencePersisted: boolean;
  standard: number;
  standard_ex_tax: number;
};

export type ShippingPreference = 'standard' | 'express';

export type Tax = {
  amount: number;
  type: string;
};

export type Totals = {
  express: number;
  standard: number;
};

export type User = {
  email: null | string;
  first_name: null | string;
  id: number;
  last_name: null | string;
};
